import { apply, toast } from '../core/effects';
import { bus } from '../core/bus';
import { S } from '../core/game';
import type { GameState } from '../core/state';
import { m } from '../i18n';
import { objName } from '../i18n/labels';
import { sfx } from '../audio/sfx';
import { say, type Choice } from '../ui/dialog';
import { openWallet } from '../ui/wallet';
import { dayNo } from './money';
import { registerObjectPrefix } from './registry';

/**
 * Lantern Bank: a real account next to the piggy bank. Deposits are safe, every morning pays
 * 2 % interest, and a savings goal rewards reaching it. Counters (`bank_*` on the town map) and
 * cash machines (`bank_atm*`) share the same dialog.
 */
export const INTEREST_RATE = 0.02;
const MAX_INTEREST = 50;
const GOALS = [100, 300, 500, 1000];
const AMOUNTS = [10, 50, 100];

export interface Account {
  bank: number;
  goal: number;
  bankLog: { day: number; kind: 'in' | 'out' | 'interest' | 'goal'; n: number }[];
}

/** The account on the save, created on first use for older saves. */
export function account(s: GameState = S()): Account {
  const mo = s.money;
  mo.bank ??= 0;
  mo.goal ??= 0;
  mo.bankLog ??= [];
  return mo as Account;
}

function log(kind: Account['bankLog'][number]['kind'], n: number, s: GameState = S()): void {
  const a = account(s);
  a.bankLog.push({ day: dayNo(s), kind, n });
  if (a.bankLog.length > 12) a.bankLog.splice(0, a.bankLog.length - 12);
}

export function bankDeposit(n: number, s: GameState = S()): boolean {
  if (!(n > 0) || s.coins < n) return false;
  const a = account(s);
  s.coins -= n;
  a.bank += n;
  log('in', n, s);
  checkGoal(s);
  bus.emit('change');
  return true;
}

export function bankWithdraw(n: number, s: GameState = S()): boolean {
  const a = account(s);
  if (!(n > 0) || a.bank < n) return false;
  a.bank -= n;
  s.coins += n;
  log('out', n, s);
  bus.emit('change');
  return true;
}

/** Morning interest: 2 % of the balance, at least 1 coin on 20+, at most 50. */
export function payBankInterest(s: GameState = S()): number {
  const a = account(s);
  if (a.bank < 20) return 0;
  const n = Math.min(MAX_INTEREST, Math.max(1, Math.round(a.bank * INTEREST_RATE)));
  a.bank += n;
  log('interest', n, s);
  checkGoal(s);
  return n;
}

function checkGoal(s: GameState = S()): void {
  const a = account(s);
  if (a.goal > 0 && a.bank >= a.goal && !s.forever[`bank.goal.${a.goal}`]) {
    s.forever[`bank.goal.${a.goal}`] = true;
    log('goal', a.goal, s);
    window.setTimeout(() => {
      apply({ stars: 5, mood: 6 });
      sfx('fanfare');
      toast(m().airport.bank.goalReached(a.goal));
    }, 300);
  }
}

bus.on('dayStart', () => {
  const n = payBankInterest();
  if (n > 0) window.setTimeout(() => toast(m().airport.bank.interest(n, account().bank)), 1800);
});

function bankDialog(id: string): void {
  const t = m().airport.bank;
  const s = S();
  const a = account(s);
  const atm = id.startsWith('bank_atm');
  const who = objName(atm ? 'bank_atm' : 'bank_counter');
  const icon = atm ? '🏧' : '🏦';
  const again = () => bankDialog(id);
  const after = (ok: boolean, poor: string) => {
    if (!ok) return toast(poor);
    sfx('checkout');
    say({ who, icon, text: t.done(account().bank), choices: [{ text: m().common.ok }, { text: t.statement, run: statement }] });
  };
  const statement = () => {
    const rows = [...account().bankLog].reverse();
    say({
      who,
      icon: '📄',
      text: rows.length ? `**${t.stmtTitle}**\n${rows.map((r) => `${r.kind === 'out' ? '−' : '+'}${r.n}🪙 · ${t.kinds[r.kind]}`).join('\n')}` : t.stmtNone,
      choices: [{ text: m().common.back, run: again }],
    });
  };
  const goal = () =>
    say({
      who,
      icon: '🎯',
      text: `${t.goalText(account().goal, account().bank)}\n${t.reward}`,
      choices: [
        ...GOALS.filter((g) => g > account().bank).map((g): Choice => ({ text: t.goalSet(g), run: () => ((account().goal = g), sfx('ok'), bus.emit('change'), again()) })),
        ...(account().goal ? [{ text: t.goalClear, run: () => ((account().goal = 0), again()) }] : []),
        { text: m().common.back, run: again },
      ],
    });
  const choices: Choice[] = [];
  for (const n of AMOUNTS) if (s.coins >= n) choices.push({ text: t.deposit(n), run: () => after(bankDeposit(n), t.poor) });
  if (s.coins > 0 && !AMOUNTS.includes(s.coins)) choices.push({ text: t.depositAll(s.coins), run: () => after(bankDeposit(S().coins), t.poor) });
  for (const n of AMOUNTS) if (a.bank >= n) choices.push({ text: t.withdraw(n), run: () => after(bankWithdraw(n), t.poorBank) });
  if (a.bank > 0 && !AMOUNTS.includes(a.bank)) choices.push({ text: t.withdrawAll(a.bank), run: () => after(bankWithdraw(account().bank), t.poorBank) });
  choices.push({ text: t.statement, run: statement }, { text: t.goal, run: goal });
  if (!atm) choices.push({ text: t.why, run: () => say({ who, icon, text: t.whyText, choices: [{ text: m().common.back, run: again }] }) });
  choices.push({ text: t.wallet, run: openWallet }, { text: m().common.back });
  say({ who, icon, text: atm ? t.atm(a.bank, s.coins) : `${t.hello(a.bank, s.coins)}${a.bank === 0 ? `\n${t.empty}` : ''}`, choices });
}

registerObjectPrefix('bank_', bankDialog);

/** How a price can be covered: from the pocket, with the bank card (pocket + account), or not at all. */
export function payPlan(price: number, s: GameState = S()): 'pocket' | 'card' | 'none' {
  if (s.coins >= price) return 'pocket';
  if (s.coins + account(s).bank >= price) return 'card';
  return 'none';
}

/** Take `price` from the pocket, topping it up from the account when the pocket is short. */
export function payFrom(price: number, s: GameState = S()): boolean {
  const plan = payPlan(price, s);
  if (plan === 'none') return false;
  if (plan === 'card') bankWithdraw(price - s.coins, s);
  s.coins -= price;
  bus.emit('change');
  return true;
}

/**
 * Charge a price in a dialog: straight from the pocket, or after offering to pay the missing part
 * with the bank card. `onPaid` runs once the coins are gone; with nothing to pay with, it explains.
 */
export function payOrCard(price: number, who: string, icon: string, onPaid: () => void, onBack?: () => void): void {
  const t = m().airport.bank;
  const s = S();
  const plan = payPlan(price, s);
  if (plan === 'none') return say({ who, icon, text: t.cannotPay(price, s.coins, account(s).bank), choices: [{ text: m().common.ok, run: onBack }] });
  if (plan === 'pocket') {
    apply({ coins: -price });
    return onPaid();
  }
  const missing = price - s.coins;
  say({
    who,
    icon,
    text: t.payCard(price, s.coins, missing, account(s).bank),
    choices: [
      { text: t.payCardBtn(missing), run: () => (payFrom(price), sfx('checkout'), onPaid()) },
      { text: m().common.back, run: onBack },
    ],
  });
}

